import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiHandler } from '../handlers';
import { DonorWithWallet } from '../models';
import { appQueryClient } from '../appQueryClient';

export const DONOR_PROFILE_QUERY_KEY = 'currentDonorProfile';

export const donorProfileQueryFn = async (): Promise<DonorWithWallet> => {
    const response = await ApiHandler.instance.donor.getProfile();
    if (!response.success) {
        throw new Error('Failed to fetch donor profile');
    }
    const donor = response.data.response.payload.donor;
    const wallets = response.data.response.payload.wallets;
    return { donor, wallets };
};

export const useDonorProfileQuery = (enabled = true) => {
    return useQuery({
        queryKey: [DONOR_PROFILE_QUERY_KEY],
        queryFn: donorProfileQueryFn,
        enabled,
        staleTime: 1000 * 60 * 5,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });
};

export const invalidateDonorProfileQuery = () => {
    return appQueryClient.invalidateQueries({
        queryKey: [DONOR_PROFILE_QUERY_KEY],
    });
};

export const getDonorProfileQueryData = () => {
    return appQueryClient.getQueryData<DonorWithWallet>([
        DONOR_PROFILE_QUERY_KEY,
    ]);
};

export const setDonorQueryData = (data: DonorWithWallet) => {
    appQueryClient.setQueryData<DonorWithWallet>(
        [DONOR_PROFILE_QUERY_KEY],
        data,
    );
};

export const clearDonorProfileQuery = () => {
    appQueryClient.removeQueries({ queryKey: [DONOR_PROFILE_QUERY_KEY] });
};

// Disabled by default: the profile is loaded once by useLoadDonor, which
// refetches when an access token is present. Pass `enabled` to let a call site
// fetch on its own.
export const useDonorProfile = (enabled = false) => {
    const queryClient = useQueryClient();
    const { data, isFetched, isFetching, isLoading, isError, error, refetch } =
        useDonorProfileQuery(enabled);

    const getDonorProfile = useCallback(
        () =>
            queryClient.getQueryData<DonorWithWallet>([
                DONOR_PROFILE_QUERY_KEY,
            ]),
        [queryClient],
    );

    const updateLocalDonorProfile = useCallback(
        (profile: DonorWithWallet | undefined) => {
            if (profile === undefined) {
                // `setQueryData` ignores an `undefined` value, so clearing the
                // profile means removing the cache entry. Careful: a mounted,
                // enabled observer refetches right after this.
                queryClient.removeQueries({
                    queryKey: [DONOR_PROFILE_QUERY_KEY],
                });
                return;
            }
            queryClient.setQueryData<DonorWithWallet>(
                [DONOR_PROFILE_QUERY_KEY],
                profile,
            );
        },
        [queryClient],
    );

    return {
        donorProfile: data,
        // any request in flight, background refetches included
        isFetching,
        // first load only: no data cached yet
        isLoading,
        // never attempted: no request has ever resolved, none in flight.
        // A failed attempt counts as an attempt, so this stays false after an
        // error. Clearing the cache entry resets it to true, but only from the
        // next render on - removal does not notify the observer.
        hasNeverFetched: !isFetched && !isFetching,
        isError,
        error,
        refetch,
        getDonorProfile,
        updateLocalDonorProfile,
    };
};
